import type {
  ModifierTarget,
  UnlockCondition,
} from "@/features/hoverIdle/core/hoverIdleDefinitions";
import { isUnlockConditionMet } from "@/features/hoverIdle/core/hoverIdleDefinitions";
import type { UnlockEvaluationContext } from "@/features/hoverIdle/core/hoverIdleDefinitions";

export type MpmAssignmentId =
  | "mhov_support"
  | "nhov_support"
  | "ohov_preparation"
  | "reset_policy"
  | "exploration_policy"
  | "challenge_policy";

export interface MpmAssignmentDefinition {
  readonly id: MpmAssignmentId;
  readonly label: string;
  readonly description: string;
  readonly unlockCondition: UnlockCondition;
  readonly modifierTarget?: ModifierTarget;
}

export const MPM_ASSIGNMENT_IDS = [
  "mhov_support",
  "nhov_support",
  "ohov_preparation",
  "reset_policy",
  "exploration_policy",
  "challenge_policy",
] as const satisfies readonly MpmAssignmentId[];

export const MPM_ASSIGNMENT_DEFINITIONS: readonly MpmAssignmentDefinition[] = [
  {
    id: "mhov_support",
    label: "MHov Support",
    description: "Use MP management capacity to support future MHov effects.",
    unlockCondition: { type: "resetCountAtLeast", layer: "mhov", count: 1 },
    modifierTarget: "mhov.effect",
  },
  {
    id: "nhov_support",
    label: "NHov Support",
    description: "Use MP management capacity to support future NHov effects.",
    unlockCondition: { type: "resetCountAtLeast", layer: "nhov", count: 1 },
    modifierTarget: "nhov.effect",
  },
  {
    id: "ohov_preparation",
    label: "OHov Preparation",
    description: "Reserve MP management capacity for future OHov preparation.",
    unlockCondition: {
      type: "or",
      conditions: [
        { type: "resourceAtLeast", resource: "hovCore", amount: 1 },
        { type: "resetCountAtLeast", layer: "ohov", count: 1 },
      ],
    },
    modifierTarget: "ohov.hovCoreGain",
  },
  {
    id: "reset_policy",
    label: "Reset Policy",
    description:
      "Reserve MP management capacity for future reset timing policies.",
    unlockCondition: { type: "resetCountAtLeast", layer: "mhov", count: 1 },
  },
  {
    id: "exploration_policy",
    label: "Exploration Policy",
    description:
      "Reserve MP management capacity for future exploration routing policies.",
    unlockCondition: { type: "skillPurchased", skillId: "unlock_exploration" },
    modifierTarget: "exploration.speed",
  },
  {
    id: "challenge_policy",
    label: "Challenge Policy",
    description:
      "Reserve MP management capacity for future challenge support policies.",
    unlockCondition: { type: "skillPurchased", skillId: "unlock_challenges" },
    modifierTarget: "challenge.reward",
  },
];

const MPM_ASSIGNMENT_ID_SET = new Set<MpmAssignmentId>(MPM_ASSIGNMENT_IDS);
const MPM_ASSIGNMENT_DEFINITION_BY_ID = new Map(
  MPM_ASSIGNMENT_DEFINITIONS.map((definition) => [definition.id, definition]),
);

export function isMpmAssignmentId(value: string): value is MpmAssignmentId {
  return MPM_ASSIGNMENT_ID_SET.has(value as MpmAssignmentId);
}

export function getMpmAssignmentDefinition(
  id: MpmAssignmentId,
): MpmAssignmentDefinition {
  const definition = MPM_ASSIGNMENT_DEFINITION_BY_ID.get(id);
  if (!definition) {
    throw new Error(`Unknown MPM assignment id: ${id}`);
  }
  return definition;
}

export function getUnlockedMpmAssignments(
  context: UnlockEvaluationContext,
): readonly MpmAssignmentDefinition[] {
  return MPM_ASSIGNMENT_DEFINITIONS.filter((definition) =>
    isUnlockConditionMet(definition.unlockCondition, context),
  );
}
